import React, { useEffect, useState } from "react";
import { getDeceasedList, findDeceasedByName } from "../api/deceased_req";
import ObituaryCard from "../components/ObituaryCard";
import Pagination from "../components/common/Pagination";
import "../style/ObituaryCard.css";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

export default function Home() {

    const [deceasedList, setDeceasedList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState({ currentPage: 1, totalPages: 1 });
    const { agencyId, page, limit } = useParams();

    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    // Get main color from first deceased or use default
    const mainColor = (deceasedList && deceasedList.length > 0 && deceasedList[0].main_color) || '#4169E1';

    const apiDeceasedList = async () => {
        setLoading(true);
        setIsSearching(false);
        const currentPage = parseInt(page) || 1;
        const currentLimit = parseInt(limit) || 20;

        const response = await getDeceasedList(agencyId, currentPage, currentLimit);

        if (response && Array.isArray(response) && response.length > 1) {
            const metadata = response[0];
            setDeceasedList(response[1]);

            // Extract or calculate total pages
            let totalPages = 1;

            // Try to find total count in metadata
            const total = metadata.total_pages || metadata.nb_pages || metadata.pages ||
                metadata.total_count || metadata.total || metadata.count ||
                metadata.nb_elements || metadata.total_items || metadata.nb_results;

            if (total) {
                // If it looks like a page count (small number)
                if (total < 1000 && (metadata.total_pages || metadata.nb_pages || metadata.pages)) {
                    totalPages = parseInt(total);
                }
                // If it looks like a total item count
                else {
                    totalPages = Math.ceil(parseInt(total) / currentLimit);
                }
            }

            // Fallback: if we are on a page > 1, totalPages must be at least that
            if (currentPage > totalPages) {
                totalPages = currentPage;
                // If we have a full page of items, assume there's at least one more
                if (response[1].length === currentLimit) {
                    totalPages = currentPage + 1;
                }
            }

            setPaginationInfo({
                currentPage: currentPage,
                totalPages: totalPages
            });
        }
        setLoading(false);
    }

    const handleInputChange = async (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (value.length >= 3) {
            setIsSearching(true);
            try {
                const response = await findDeceasedByName(agencyId, value);
                if (response && Array.isArray(response)) {
                    setSearchResults(response);
                    setShowDropdown(true);
                } else {
                    setSearchResults([]);
                    setShowDropdown(false);
                }
            } catch (error) {
                console.error("Error searching deceased:", error);
                setSearchResults([]);
            }
        } else {
            setIsSearching(false);
            setSearchResults([]);
            setShowDropdown(false);
            if (value.length === 0) {
                apiDeceasedList();
            }
        }
    }

    const handleSelectDeceased = (deceased) => {
        // Navigate to detail page
        window.location.href = `/avis-deces/${agencyId}/1/${deceased.id}`;
    }

    const handleClearSearch = () => {
        setSearchTerm('');
        setIsSearching(false);
        setSearchResults([]);
        setShowDropdown(false);
        apiDeceasedList();
    }

    useEffect(() => {
        if (!isSearching && searchTerm.length < 3) {
            apiDeceasedList();
        }
    }, [agencyId, page, limit]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.search-container')) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="home-container">
            <div className="home-header">
                <h1>Avis de décès</h1>

                <div className="search-container">
                    <div className="search-input-wrapper">
                        <input
                            type="text"
                            placeholder="Rechercher un défunt par nom..."
                            value={searchTerm}
                            onChange={handleInputChange}
                            className="search-input"
                            onFocus={() => {
                                if (searchTerm.length >= 3 && searchResults.length > 0) {
                                    setShowDropdown(true);
                                }
                            }}
                        />
                        <FontAwesomeIcon icon={faSearch} className="search-icon-input" style={{ color: mainColor }} />
                        {searchTerm && (
                            <button type="button" onClick={handleClearSearch} className="clear-button-input">
                                &times;
                            </button>
                        )}
                    </div>

                    {showDropdown && searchResults.length > 0 && (
                        <div className="search-dropdown">
                            {searchResults.map((deceased) => (
                                <div
                                    key={deceased.id}
                                    className="search-dropdown-item"
                                    onClick={() => handleSelectDeceased(deceased)}
                                >
                                    <div className="search-item-name">
                                        {deceased.first_name} {deceased.last_name}
                                    </div>
                                    <div className="search-item-date">
                                        Décédé(e) le {new Date(deceased.death_date.date).toLocaleDateString('fr-FR')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {showDropdown && isSearching && searchResults.length === 0 && searchTerm.length >= 3 && (
                        <div className="search-dropdown">
                            <div className="search-dropdown-item no-results">
                                Aucun résultat trouvé
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="loader" style={{ borderTopColor: mainColor }}></div>
                    <p>Chargement des avis de décès...</p>
                </div>
            ) : (
                <>
                    {deceasedList.length === 0 ? (
                        <div className="no-results">
                            <p>Aucun avis de décès trouvé.</p>
                        </div>
                    ) : (
                        <div className="obituary-grid">
                            {Array.isArray(deceasedList) && deceasedList.map((deceased, index) => {
                                const formatDate = (dateObj) => {
                                    if (!dateObj || !dateObj.date) return '';
                                    const date = new Date(dateObj.date);
                                    return date.toLocaleDateString('fr-FR', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    });
                                };

                                const currentDate = formatDate(deceased.death_date);
                                const prevDate = index > 0 ? formatDate(deceasedList[index - 1].death_date) : null;
                                const showHeader = index === 0 || currentDate !== prevDate;

                                return (
                                    <React.Fragment key={deceased.id}>
                                        {showHeader && (
                                            <div className="death-header" style={{
                                                marginBottom: '10px',
                                                marginTop: index > 0 ? '30px' : '0',
                                                textAlign: 'center',
                                                width: '100%'
                                            }}>
                                                <div style={{ display: 'inline-block' }}>
                                                    <div style={{ marginBottom: '5px', textAlign: 'center' }}>
                                                        <img src="/assets/mini-oef.jpg" alt="OEF" style={{ height: '30px', width: 'auto' }} />
                                                    </div>
                                                    <p className="death-header-text" style={{ color: mainColor, fontSize: '1.1rem', margin: 0 }}>
                                                        Ils nous ont quittés le {currentDate}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        <ObituaryCard deceased={deceased} agencyId={agencyId} />
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}

                    {!isSearching && (
                        <Pagination
                            currentPage={paginationInfo.currentPage}
                            totalPages={paginationInfo.totalPages}
                            getLink={(page) => `/listes-avis-deces/${agencyId}/${page}`}
                            mainColor={mainColor}
                        />
                    )}
                </>
            )}
        </div>
    );
}